import { Box, FormControlLabel, Switch, TextField } from '@mui/material'
import { Form, Formik, FormikValues } from 'formik'
import React from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../../../app/hooks'
import { useCreateAssetCategoryMutation, useUpdateAssetCategoryMutation } from '../../../../../app/redux-fetch/apiAssets'
import { useGetTransportModeQuery } from '../../../../../app/redux-fetch/apiCoreQuery'
import SubmitButtons from '../../../../../components/Buttons/SubmitButtons'
import FormDropdown from '../../../../../components/FormDropDown/FormDropdown'
import RequiredAsterisk from '../../../../../components/RequiredAsterisk/RequiredAsterisk'
import { SectionLabelTypography } from '../../../../../components/Typographies/styles'
import { useSideModalContext } from '../../../../Assets/modals/SideModalContext'
import { fetchLoggedInUserSelector } from '../../../../selectors'
import { ILoggedInUser } from '../../../../types'
import { messages } from '../../messages'
import { AssetCategory } from '../../types'
import { getAssetCategoryValidationSchema } from './validationSchema'


type AssetCategoryFormProps = {
    assetCategory?: AssetCategory
}

const AssetCategoryForm = ({
    assetCategory,
}: AssetCategoryFormProps) => {
    const { formatMessage } = useIntl()

    const { data: transportModes = [] } = useGetTransportModeQuery()

    const { tenantId } = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)

    const isNew = !assetCategory?.id
    const validationSchema = getAssetCategoryValidationSchema(formatMessage)

    const [createAssetCategory] = useCreateAssetCategoryMutation()
    const [updateAssetCategory] = useUpdateAssetCategoryMutation()

    const { onClose } = useSideModalContext()

    return (
        <Formik
            enableReinitialize
            initialValues={{ ...assetCategory } as AssetCategory}
            validationSchema={validationSchema}
            onSubmit={async (values: FormikValues) => {
                if (isNew) {
                    await createAssetCategory({ tenantId, body: values as AssetCategory })
                } else {
                    await updateAssetCategory({ tenantId, body: values as AssetCategory })
                }
                onClose()
            }}
        >
            {({
                values,
                errors,
                handleChange,
                setFieldValue,
            }) => {
                return (
                    <Form>
                        <Box sx={{ m: '32px' }}>
                            <Box sx={{ mb: '16px' }}>
                                <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
                                    {formatMessage(messages.categoryName)}
                                    <RequiredAsterisk />
                                </SectionLabelTypography>
                                <TextField
                                    fullWidth
                                    id={'name'}
                                    name={'name'}
                                    value={values?.name ?? ''}
                                    onChange={handleChange}
                                    helperText={errors?.name as React.ReactNode}
                                    error={Boolean(errors?.name)}
                                />
                            </Box>
                            <Box sx={{ mb: '16px' }}>
                                <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
                                    {formatMessage(messages.transportMode)}
                                    <RequiredAsterisk />
                                </SectionLabelTypography>
                                <FormDropdown
                                    id={'transportModeId'}
                                    items={transportModes ?? []}
                                    onChange={(_e: any, name: string, value: number) => setFieldValue(name, value)}
                                    value={values.transportModeId}
                                    error={Boolean(errors?.transportModeId)}
                                    errorText={errors?.transportModeId}
                                />
                            </Box>
                            <Box sx={{ mb: '16px' }}>
                                <SectionLabelTypography variant='subtitle1' sx={{ mb: '4px' }}>
                                    {formatMessage(messages.enabledDisabled)}
                                </SectionLabelTypography>
                                <FormControlLabel
                                    sx={{ justifyContent: 'end', minHeight: '56px' }}
                                    value={values.enabled}
                                    labelPlacement='end'
                                    control={
                                        <Switch
                                            color='primary'
                                            checked={values.enabled}
                                            value={values.enabled}
                                            onChange={({ target }) => {
                                                setFieldValue('enabled', target.checked)
                                            }}
                                        />
                                    }
                                    label={'Enabled'}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                width: '100%',
                                padding: '16px',
                                backgroundColor: '#fff',
                                borderTop: '1px solid #e0e0e0',
                            }}
                        >
                            <SubmitButtons create={isNew} isEnabled={true} onCancel={onClose} />
                        </Box>
                    </Form>
                )
            }}
        </Formik >
    )
}

export default AssetCategoryForm
